import type {
  LocalAnaestheticCase,
  LocalCase,
  LocalCommunication,
  LocalEdtEntry,
  LocalProcedure,
} from '@iculogbook/shared';

import { db } from '../db.ts';
import type { OutboxItem } from '../db.ts';
import { tableFor } from './internal.ts';
import { hasGivenUp } from '../../sync/backoff.ts';

/**
 * The Dexie side of sync. Components never call any of this; the sync engine
 * does, and it is the only thing that does.
 *
 * The rule that matters here: **a record written because the server sent it
 * must not queue an outbox item.** Every other write path in the app pairs a
 * write with an outbox item, deliberately. This one must not, or a pull would
 * push everything straight back and the two devices would trade the same
 * records forever.
 */

export type PendingBatch = {
  items: OutboxItem[];
  cases: LocalCase[];
  procedures: LocalProcedure[];
  edtEntries: LocalEdtEntry[];
  anaestheticCases: LocalAnaestheticCase[];
  communications: LocalCommunication[];
};

export const syncRepository = {
  /**
   * The next batch to push: pending outbox items, oldest first, with the
   * records they refer to. One entry may have several queued edits; only the
   * current state of the record is sent, so duplicates collapse.
   */
  async nextBatch(userId: string, limit: number): Promise<PendingBatch> {
    const queued = await db.outbox.where('status').equals('pending').sortBy('seq');
    const mine = queued.filter((item) => item.userId === userId).slice(0, limit);

    const caseIds = [...new Set(mine.filter((i) => i.entity === 'case').map((i) => i.entityId))];
    const procedureIds = [
      ...new Set(mine.filter((i) => i.entity === 'procedure').map((i) => i.entityId)),
    ];
    const edtIds = [...new Set(mine.filter((i) => i.entity === 'edt').map((i) => i.entityId))];
    const anaestheticIds = [
      ...new Set(mine.filter((i) => i.entity === 'anaesthetic').map((i) => i.entityId)),
    ];
    const communicationIds = [
      ...new Set(mine.filter((i) => i.entity === 'communication').map((i) => i.entityId)),
    ];

    const [cases, procedures, edtEntries, anaestheticCases, communications] = await Promise.all([
      db.cases.bulkGet(caseIds),
      db.procedures.bulkGet(procedureIds),
      db.edtEntries.bulkGet(edtIds),
      db.anaestheticCases.bulkGet(anaestheticIds),
      db.communications.bulkGet(communicationIds),
    ]);

    return {
      items: mine,
      cases: cases.filter(
        (record): record is LocalCase => record !== undefined && record.userId === userId,
      ),
      procedures: procedures.filter(
        (record): record is LocalProcedure => record !== undefined && record.userId === userId,
      ),
      edtEntries: edtEntries.filter(
        (record): record is LocalEdtEntry => record !== undefined && record.userId === userId,
      ),
      anaestheticCases: anaestheticCases.filter(
        (record): record is LocalAnaestheticCase =>
          record !== undefined && record.userId === userId,
      ),
      communications: communications.filter(
        (record): record is LocalCommunication => record !== undefined && record.userId === userId,
      ),
    };
  },

  /**
   * Clears the outbox items for records the server took, and stamps those
   * records as synced.
   *
   * `syncedAt` is only set if the record has not been edited again since the
   * push started — a stale stamp would tell the merge that a genuinely pending
   * edit was safe on the server, and the next pull would overwrite it.
   */
  async settle(
    userId: string,
    settled: readonly string[],
    pushedAt: Date,
    updatedAtById: ReadonlyMap<string, Date>,
  ): Promise<void> {
    if (settled.length === 0) return;

    await db.transaction(
      'rw',
      [db.cases, db.procedures, db.edtEntries, db.anaestheticCases, db.communications, db.outbox],
      async () => {
        for (const entityId of settled) {
          const pushedUpdatedAt = updatedAtById.get(entityId);

          for (const table of [
            db.cases,
            db.procedures,
            db.edtEntries,
            db.anaestheticCases,
            db.communications,
          ]) {
            const record = await table.get(entityId);
            if (record === undefined || record.userId !== userId) continue;

            if (
              pushedUpdatedAt !== undefined &&
              record.updatedAt.getTime() === pushedUpdatedAt.getTime()
            ) {
              await table.update(entityId, { syncedAt: pushedAt });
            }
          }

          const items = await db.outbox.where('entityId').equals(entityId).toArray();
          for (const item of items) {
            if (item.userId !== userId || item.status !== 'pending' || item.seq === undefined) {
              continue;
            }

            const queuedRecord = await tableFor(item.entity).get(entityId);
            // A newer local edit arrived while this push was in flight. Leave its
            // outbox item alone so the change still reaches the server.
            if (
              queuedRecord !== undefined &&
              pushedUpdatedAt !== undefined &&
              queuedRecord.updatedAt.getTime() > pushedUpdatedAt.getTime()
            ) {
              continue;
            }

            await db.outbox.delete(item.seq);
          }
        }
      },
    );
  },

  /** Records a failed attempt. At ten consecutive failures the item is surfaced. */
  async recordFailure(items: readonly OutboxItem[], error: string): Promise<void> {
    await db.transaction('rw', db.outbox, async () => {
      for (const item of items) {
        if (item.seq === undefined) continue;

        const attempts = item.attempts + 1;
        await db.outbox.update(item.seq, {
          attempts,
          lastError: error,
          status: hasGivenUp(attempts) ? 'failed' : 'pending',
        });
      }
    });
  },

  /** Puts failed items back in the queue, for the manual retry in Settings. */
  async retryFailed(userId: string): Promise<number> {
    const failed = await db.outbox.where('status').equals('failed').toArray();
    const mine = failed.filter((item) => item.userId === userId);

    await db.transaction('rw', db.outbox, async () => {
      for (const item of mine) {
        if (item.seq === undefined) continue;
        await db.outbox.update(item.seq, { status: 'pending', attempts: 0 });
      }
    });

    return mine.length;
  },

  /**
   * Writes records that came from the server. **No outbox items** — see the
   * note at the top of this file.
   */
  async applyPulled(
    userId: string,
    cases: readonly LocalCase[],
    procedures: readonly LocalProcedure[],
    edtEntries: readonly LocalEdtEntry[] = [],
    anaestheticCases: readonly LocalAnaestheticCase[] = [],
    communications: readonly LocalCommunication[] = [],
  ): Promise<void> {
    if (
      cases.length === 0 &&
      procedures.length === 0 &&
      edtEntries.length === 0 &&
      anaestheticCases.length === 0 &&
      communications.length === 0
    ) {
      return;
    }

    await db.transaction(
      'rw',
      db.cases,
      db.procedures,
      db.edtEntries,
      db.anaestheticCases,
      db.communications,
      async () => {
        // Cases before procedures, so a nested procedure never lands ahead of the
        // case it belongs to. EDT entries and communications reference nothing,
        // so their position here is arbitrary.
        if (cases.length > 0)
          await db.cases.bulkPut(cases.map((record) => ({ ...record, userId })));
        if (procedures.length > 0) {
          await db.procedures.bulkPut(procedures.map((record) => ({ ...record, userId })));
        }
        if (edtEntries.length > 0) {
          await db.edtEntries.bulkPut(edtEntries.map((record) => ({ ...record, userId })));
        }
        if (anaestheticCases.length > 0) {
          await db.anaestheticCases.bulkPut(
            anaestheticCases.map((record) => ({ ...record, userId })),
          );
        }
        if (communications.length > 0) {
          await db.communications.bulkPut(
            communications.map((record) => ({ ...record, userId })),
          );
        }
      },
    );
  },

  async localsById(
    ids: readonly string[],
  ): Promise<Map<string, { updatedAt: Date; syncedAt: Date | null }>> {
    const [cases, procedures, edtEntries, anaestheticCases, communications] = await Promise.all([
      db.cases.bulkGet([...ids]),
      db.procedures.bulkGet([...ids]),
      db.edtEntries.bulkGet([...ids]),
      db.anaestheticCases.bulkGet([...ids]),
      db.communications.bulkGet([...ids]),
    ]);

    const map = new Map<string, { updatedAt: Date; syncedAt: Date | null }>();
    for (const record of [
      ...cases,
      ...procedures,
      ...edtEntries,
      ...anaestheticCases,
      ...communications,
    ]) {
      if (record !== undefined) map.set(record.id, record);
    }

    return map;
  },
};
